import React from 'react';

/**
 * THE HIRES PAGES' ORDER MENU — order and grouping (owner ruling 2026-09-28).
 * He looked at the Dept Orders menu with `Off hire all` bolted on the bottom
 * and said *"I think this needs to be changed for Hires. The order, what
 * people expect next in the list etc"*, then chose this layout:
 *
 *   WHILE ANY HIRE LINE IS STILL ON HIRE (not off_hired)
 *   ─ Hire ─      Off hire all (N lines) · Extend all…
 *   ─ Next step ─ the order's normal next step (Mark Ordered, …), AFTER Hire
 *   ─ Order ─     View in orders (was View Details, 2026-10-03) · Edit
 *   ─ Status ─    the status override (`Override order`)
 *   ─ Export ─    Export CSV · Download PDF · Print… · Share…
 *
 *   ONCE EVERY HIRE LINE IS OFF HIRED
 *   ─ Next step ─ Mark as Complete
 *   ─ Hire ─      Bring all back on hire
 *   ─ Order ─ · ─ Status ─ · ─ Export ─
 *
 * 🔑 INVARIANT: THIS RUNS ONLY ON THE HIRES PAGES. `withHireMenu(null, …)`
 * returns the props object it was given, untouched — the SAME object — so
 * Department Orders / My Orders / Approvals (which pass no `hireActions`)
 * render exactly the menu they rendered before. Pinned by §5 of
 * `HireOrderExpansion.menuOrder.test.js` and by both pages' parity goldens.
 *
 * 🚨 Every item arrives FINISHED AND GATED by the page (`OrderActionsMenu`
 * resolves nothing; categories are presentation). This module MOVES items
 * between sections and adds the hire items. The ONE thing it removes is
 * `Mark as Complete` while a hire line is still out — a hire order is not
 * finished while kit is still on hire (the ruling). That is a filter written
 * here on purpose, never a category that hides it.
 *
 * The status override moves from the Next-step FOOTER (Dept) or the flat
 * menu's footer (My Orders) into its own `Status` section as a render item,
 * so it keeps the label the owner chose for it (`Override order`, see
 * `client/src/pages/CLAUDE.md`) and gains no second rule under its heading.
 */

// Which section each ORDER item belongs in on a hires page. Anything not
// named here is a lifecycle step and goes under Next step.
const SECTION_OF = {
  view: 'order',
  edit: 'order',
  delete: 'order',
  'export-csv': 'export',
  pdf: 'export',
  cancel: 'status',
};

/**
 * @param {{ categories?: object[], items: any[], footer?: any }} props — the
 *   page's own `OrderActionsMenu` props (categories absent on My Orders).
 * @param {{ hireItems: object[], allOffHired: boolean }} hire — the hire
 *   section's finished items, and whether every hire line is off hired
 *   (false when the order has none).
 * @returns the same shape, arranged for a hires page.
 */
export function arrangeHireOrderMenu(props, { hireItems, allOffHired, viewOrder = null }) {
  const cats = props.categories || [];
  const next = cats.find((c) => c.key === 'next');
  const statusControl = (next && next.footer) || props.footer || null;

  const items = (props.items || [])
    .filter(Boolean)
    .filter((it) => allOffHired || it.key !== 'complete')
    .map((it) => ({ ...it, category: SECTION_OF[it.key] || 'next' }))
    // 🔀 2026-10-03 (owner): the PO/Order cell no longer links to the order
    // (a tap expands the hire), so the page's `View Details` item — same
    // gate, same testid — becomes THE way there: `View in orders`, the
    // page's own `handleViewOrder` deep link (`/department/orders?order=`
    // or `/orders?order=`), never re-derived here.
    .map((it) => (it.key === 'view' && viewOrder ? {
      ...it,
      label: 'View in orders',
      title: 'Open this order in the orders list',
      onClick: viewOrder,
    } : it));

  const statusItem = statusControl ? [{
    key: 'status-override',
    category: 'status',
    render: (close) => (
      <div className="px-3 py-2">{typeof statusControl === 'function' ? statusControl(close) : statusControl}</div>
    ),
  }] : [];

  const nextCat = { key: 'next', label: 'Next step' };
  const hireCat = { key: 'hire', label: 'Hire' };
  return {
    categories: [
      ...(allOffHired ? [nextCat, hireCat] : [hireCat, nextCat]),
      { key: 'order', label: 'Order' },
      { key: 'status', label: 'Status' },
      { key: 'export', label: 'Export' },
    ],
    items: [...items, ...hireItems.map((it) => ({ ...it, category: 'hire' })), ...statusItem],
    footer: null,
  };
}

/**
 * The hooks' one call: `{...withHireMenu(hireActions, order, { dirty }, { categories, items })}`
 * on `OrderActionsMenu`. No `hireActions` → the props, unchanged.
 */
export function withHireMenu(hireActions, order, opts, props) {
  return hireActions ? hireActions.arrangeMenu(order, opts, props) : props;
}
